import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { authenticator } from 'otplib';
import { getFromStorage, saveToStorage, removeFromStorage } from '../utils/storage';
import { decryptData } from '../utils/crypto';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Box, Typography, Card, CardContent, TextField, Button, AppBar, Toolbar,
  IconButton, CircularProgress, Stack, Divider, Alert,
  InputAdornment, Tooltip, styled
} from '@mui/material';
import {
  Google, Phone, Lock, RestartAlt, Wallet, ExitToApp, ArrowBack,
  ContentCopy, Visibility, VisibilityOff
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

// Styled card component with glassmorphism effect
const GlassCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  backgroundColor: '#1a1a1a',
  border: '1px solid #333333',
  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.4)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.5)'
  }
}));

// Styled button component with monochromatic gradient
const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(90deg, #ffffff 0%, #cccccc 100%)',
  backgroundSize: '200% auto',
  color: '#000000',
  borderRadius: '12px',
  padding: '12px 24px',
  fontWeight: 500,
  textTransform: 'none',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundPosition: 'right center',
    boxShadow: '0 6px 12px rgba(0, 0, 0, 0.4)',
    transform: 'translateY(-2px)'
  },
  '&:active': {
    transform: 'translateY(0)'
  },
  '&.Mui-disabled': {
    background: '#333333',
    color: '#666666',
    boxShadow: 'none'
  }
}));

// Dashboard card component
const DashboardCard = ({ children, sx = {} }) => (
  <GlassCard 
    elevation={0} 
    sx={{
      p: 3,
      minHeight: '100%',
      ...sx
    }}
  >
    <CardContent sx={{ p: 0, height: '100%' }}>
      {children}
    </CardContent>
  </GlassCard>
);

function Setup2FA({ wallet, setWallet, setIsSessionActive }) {
  const [step, setStep] = useState('setup');
  const [secret, setSecret] = useState('');
  const [otpAuthUrl, setOtpAuthUrl] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [resetPassword, setResetPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const navigate = useNavigate();

  // Define steps for progress bar
  const steps = ['setup', 'verified'];

  useEffect(() => {
    const setup2FA = async () => {
      if (!wallet || !wallet.address || !wallet.address.match(/^0x[a-fA-F0-9]{40}$/)) {
        toast.error('No valid wallet detected. Please create a new one.', {
          position: 'top-center',
          style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
        });
        navigate('/create-wallet');
        return;
      }

      try {
        const stored2FA = await getFromStorage(`secure_v2_2faSecret_0x${wallet.address}`);
        if (stored2FA) {
          console.log('DEBUG: Existing 2FA secret found:', stored2FA);
          setIsVerified(true);
          setSecret(stored2FA);
          setStep('verified');
          toast.info('2FA is already set up for this wallet.', {
            position: 'top-center',
            style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
          });
          return;
        }

        const newSecret = authenticator.generateSecret();
        setSecret(newSecret);
        const url = authenticator.keyuri(wallet.address, 'AVO_STATION', newSecret);
        setOtpAuthUrl(url);
        console.log('DEBUG: New 2FA secret generated:', newSecret);
        console.log('DEBUG: OTP Auth URL:', url);
      } catch (error) {
        console.error('DEBUG: Error setting up 2FA:', error);
        toast.error('Error initiating 2FA setup.', {
          position: 'top-center',
          style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
        });
      } finally {
        setIsLoading(false);
      }
    };

    setup2FA();
  }, [wallet, navigate]);

  const verify2FACode = async () => {
    if (!verificationCode || !/^\d{6}$/.test(verificationCode)) {
      toast.error('Please enter a valid 6-digit code.', {
        position: 'top-center',
        style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
      });
      return;
    }

    setIsLoading(true);
    try {
      const isValid = authenticator.check(verificationCode, secret);
      if (isValid) {
        await saveToStorage(`secure_v2_2faSecret_0x${wallet.address}`, secret);
        console.log('DEBUG: 2FA secret saved:', secret);

        await saveToStorage('sessionActive', {
          active: true,
          lastLogin: new Date().toISOString(),
          walletAddress: wallet.address,
        });
        setIsSessionActive(true);

        setIsVerified(true);
        setStep('verified');
        toast.success('2FA set up successfully!', {
          position: 'top-center',
          style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
        });
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        toast.error('Invalid verification code.', {
          position: 'top-center',
          style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
        });
      }
    } catch (error) {
      console.error('DEBUG: Error verifying 2FA code:', error);
      toast.error('Error verifying code.', {
        position: 'top-center',
        style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset2FA = async () => {
    if (!resetPassword) {
      toast.error('Please enter your wallet password.', {
        position: 'top-center',
        style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
      });
      return;
    }

    setIsLoading(true);
    try {
      const encryptedWallet = await getFromStorage('cryptoWallet');
      if (!encryptedWallet) {
        throw new Error('Wallet not found in storage.');
      }
      await decryptData(encryptedWallet, resetPassword); // Validate password

      const newSecret = authenticator.generateSecret();
      setSecret(newSecret);
      const url = authenticator.keyuri(wallet.address, 'AVO_STATION', newSecret);
      setOtpAuthUrl(url);
      setIsVerified(false);
      setStep('setup');
      setResetPassword('');
      setIsResetting(false);
      toast.info('2FA secret reset. Scan the new QR code.', {
        position: 'top-center',
        style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
      });
    } catch (error) {
      console.error('DEBUG: Error resetting 2FA:', error);
      toast.error('Incorrect password or error resetting 2FA.', {
        position: 'top-center',
        style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret);
    toast.success('Secret copied to clipboard.', {
      position: 'top-center',
      style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
    });
  };

  const handleMenuClose = () => {
    // Function kept for compatibility but no longer needed
  };

  const handleLogout = async () => {
    try {
      await saveToStorage('sessionActive', {
        active: false,
        lastLogout: new Date().toISOString(),
        walletAddress: wallet.address,
      });
      setWallet(null);
      setIsSessionActive(false);
      toast.success('Logged out successfully.', {
        position: 'top-center',
        style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
      });
      navigate('/login');
    } catch (error) {
      console.error('DEBUG: Error logging out:', error);
      toast.error('Error logging out.', {
        position: 'top-center',
        style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
      });
    } finally {
      // No need to close menu anymore
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#000000', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
      <ToastContainer 
        limit={3} 
        newestOnTop 
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        draggable
        style={{ zIndex: 1400 }}
        toastStyle={{
          backgroundColor: '#1a1a1a',
          color: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 6px 12px rgba(0, 0, 0, 0.4)',
        }}
      />

      {/* Top bar */}
      <AppBar 
        position="sticky" 
        elevation={0} 
        sx={{ 
          bgcolor: '#000000',
          borderBottom: '1px solid #FFFFFF',
          boxShadow: 'none'
        }}
      >
        <Toolbar sx={{ minHeight: 48, justifyContent: 'space-between', py: 1, gap: 2 }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 500,
            color: '#FFFFFF',
            fontSize: '1.4rem',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            letterSpacing: '2px',
            textTransform: 'uppercase'
          }}>
            AVO_STATION
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              onClick={() => navigate('/dashboard')}
              sx={{
                color: '#FFFFFF',
                border: '1px solid #FFFFFF',
                borderRadius: 0,
                px: 2,
                py: 0.5,
                fontSize: '0.875rem',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: '#FFFFFF',
                  color: '#000000'
                }
              }}
            >
              DASHBOARD
            </Button>
            <Button
              onClick={handleLogout}
              sx={{
                color: '#FFFFFF',
                border: '1px solid #FFFFFF',
                borderRadius: 0,
                px: 2,
                py: 0.5,
                fontSize: '0.875rem',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: '#FFFFFF',
                  color: '#000000'
                }
              }}
            >
              LOG OUT
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main content */}
      <Box sx={{ maxWidth: 800, mx: 'auto', px: 4, py: 4, position: 'relative', zIndex: 1, flexGrow: 1 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress sx={{ color: '#ffffff' }} />
          </Box>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 600, mb: 3, color: '#ffffff', fontSize: '2.2rem' }}>
              Set Up Two-Factor Authentication
            </Typography>

            {/* Progress bar */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                {steps.map((s, idx) => (
                  <Box
                    key={s}
                    sx={{
                      flex: 1,
                      height: 3,
                      borderRadius: 2,
                      bgcolor: step === s || steps.indexOf(step) > idx ? '#ffffff' : '#666666',
                      transition: 'background-color 0.3s',
                    }}
                  />
                ))}
              </Stack>
            </Box>

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                transition={{ duration: 0.3 }}
              >
                <DashboardCard>
                  {step === 'setup' && !isVerified && (
                    <>
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#ffffff', fontSize: '1.8rem' }}>
                        Set Up Two-Factor Authentication
                      </Typography>
                      <Typography variant="body1" color="#999999" sx={{ mb: 3, fontSize: '1.0rem' }}>
                        Scan the QR code with your authenticator app.
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        {otpAuthUrl && (
                          <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: '10px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)' }}>
                            <QRCodeSVG
                              value={otpAuthUrl}
                              size={160}
                              level="H"
                              includeMargin={true}
                              bgColor="#ffffff"
                              fgColor="#000000"
                            />
                          </Box>
                        )}
                      </Box>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: '#ffffff', fontSize: '1.4rem' }}>
                        Scan with:
                      </Typography>
                      <Stack spacing={1} sx={{ mb: 2 }}>
                        {[
                          { icon: <Google sx={{ color: '#ffffff' }} />, label: 'Google Authenticator' },
                          { icon: <Phone sx={{ color: '#ffffff' }} />, label: 'Authy' },
                          { icon: <Lock sx={{ color: '#ffffff' }} />, label: 'Microsoft Authenticator' },
                        ].map(({ icon, label }) => (
                          <Box key={label} display="flex" alignItems="center" gap={1}>
                            {icon}
                            <Typography variant="body2" color="#999999" sx={{ fontSize: '0.9rem' }}>
                              {label}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                      <Divider sx={{ my: 2, borderColor: '#333333' }} />
                      <Typography variant="body2" gutterBottom sx={{ fontWeight: 500, color: '#ffffff', fontSize: '0.9rem' }}>
                        Or enter manually:
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#ffffff', fontSize: '0.9rem' }}>
                          {secret.match(/.{1,4}/g)?.join(' ') || ''}
                        </Typography>
                        <Tooltip title="Copy secret">
                          <IconButton 
                            onClick={handleCopySecret}
                            sx={{ 
                              color: '#ffffff',
                              '&:hover': { backgroundColor: '#333333', color: '#cccccc' },
                              '&:focus': { backgroundColor: '#333333', color: '#cccccc' },
                              '&.Mui-focusVisible': { backgroundColor: '#333333', color: '#cccccc', outline: '2px solid #cccccc' }
                            }}
                            disableRipple
                          >
                            <ContentCopy sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Divider sx={{ my: 3, borderColor: '#333333' }} />
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: '#ffffff', fontSize: '1.4rem' }}>
                        Enter Verification Code
                      </Typography>
                      <Typography variant="body2" color="#999999" sx={{ mb: 2, fontSize: '0.9rem' }}>
                        Type the 6-digit code from your authenticator app.
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Verification Code"
                        value={verificationCode}
                        onChange={(e) => {
                          if (/^\d{0,6}$/.test(e.target.value)) {
                            setVerificationCode(e.target.value);
                          }
                        }}
                        inputProps={{
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          maxLength: 6,
                        }}
                        sx={{ 
                          mb: 3,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: '#222222',
                            '& fieldset': { borderColor: '#333333' },
                            '&:hover fieldset': { borderColor: '#666666' },
                            '&.Mui-focused fieldset': { borderColor: '#ffffff' },
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '1.0rem',
                            color: '#999999',
                          },
                          '& .MuiInputBase-input': {
                            fontSize: '1.2rem',
                            padding: '12px',
                            color: '#ffffff',
                            textAlign: 'center',
                            letterSpacing: 4,
                          }
                        }}
                      />
                      <GradientButton
                        fullWidth
                        size="large"
                        onClick={verify2FACode}
                        disabled={isLoading || verificationCode.length !== 6}
                        endIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
                      >
                        Verify Code
                      </GradientButton>
                      <Alert 
                        severity="warning" 
                        sx={{ 
                          mt: 2, 
                          borderRadius: '12px',
                          bgcolor: '#222222',
                          color: '#ffffff',
                          '& .MuiAlert-icon': { color: '#999999' },
                          border: '1px solid #333333'
                        }}
                      >
                        <strong>Important:</strong> Save your 2FA setup securely.
                      </Alert>
                    </>
                  )}

                  {step === 'verified' && isVerified && (
                    <>
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#ffffff', fontSize: '1.8rem' }}>
                        2FA Configured
                      </Typography>
                      <Typography variant="body1" color="#999999" sx={{ mb: 3, fontSize: '1.0rem' }}>
                        Two-factor authentication is active for your wallet.
                      </Typography>
                      <Alert 
                        severity="success" 
                        sx={{ 
                          mb: 3, 
                          borderRadius: '12px',
                          bgcolor: '#222222',
                          color: '#ffffff',
                          '& .MuiAlert-icon': { color: '#ffffff' },
                          border: '1px solid #333333'
                        }}
                      >
                        Your wallet is protected with 2FA. You will be redirected to the dashboard.
                      </Alert>
                      {isResetting ? (
                        <>
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: '#ffffff', fontSize: '1.4rem' }}>
                            Reset 2FA
                          </Typography>
                          <Typography variant="body2" color="#999999" sx={{ mb: 2, fontSize: '0.9rem' }}>
                            Enter your wallet password to authorize the reset.
                          </Typography>
                          <TextField
                            fullWidth
                            variant="outlined"
                            label="Wallet Password"
                            type={showPassword ? 'text' : 'password'}
                            value={resetPassword}
                            onChange={(e) => setResetPassword(e.target.value)}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton 
                                    onClick={() => setShowPassword(!showPassword)}
                                    sx={{ 
                                      color: '#ffffff',
                                      '&:hover': { backgroundColor: '#333333', color: '#cccccc' },
                                      '&:focus': { backgroundColor: '#333333', color: '#cccccc' },
                                      '&.Mui-focusVisible': { backgroundColor: '#333333', color: '#cccccc', outline: '2px solid #cccccc' }
                                    }}
                                    disableRipple
                                  >
                                    {showPassword ? <VisibilityOff sx={{ fontSize: '20px' }} /> : <Visibility sx={{ fontSize: '20px' }} />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            sx={{ 
                              mb: 3,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                backgroundColor: '#222222',
                                '& fieldset': { borderColor: '#333333' },
                                '&:hover fieldset': { borderColor: '#666666' },
                                '&.Mui-focused fieldset': { borderColor: '#ffffff' },
                              },
                              '& .MuiInputLabel-root': {
                                fontSize: '1.0rem',
                                color: '#999999',
                              },
                              '& .MuiInputBase-input': {
                                fontSize: '1.0rem',
                                padding: '12px',
                                color: '#ffffff'
                              }
                            }}
                          />
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <GradientButton
                              fullWidth
                              startIcon={<RestartAlt />}
                              onClick={handleReset2FA}
                              disabled={isLoading || !resetPassword}
                            >
                              Reset 2FA
                            </GradientButton>
                            <Button
                              fullWidth
                              variant="outlined"
                              onClick={() => setIsResetting(false)}
                              disabled={isLoading}
                              sx={{
                                borderRadius: '12px',
                                py: 1.5,
                                fontWeight: 500,
                                color: '#ffffff',
                                borderColor: '#333333',
                                '&:hover': { borderColor: '#666666', backgroundColor: '#333333' },
                                '&:focus': { borderColor: '#666666', backgroundColor: '#333333' },
                                '&.Mui-disabled': { borderColor: '#333333', color: '#666666' }
                              }}
                            >
                              Cancel
                            </Button>
                          </Stack>
                        </>
                      ) : (
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<RestartAlt />}
                          onClick={() => setIsResetting(true)}
                          disabled={isLoading}
                          sx={{
                            borderRadius: '12px',
                            py: 1.5,
                            fontWeight: 500,
                            color: '#ffffff',
                            borderColor: '#333333',
                            '&:hover': { borderColor: '#666666', backgroundColor: '#333333' },
                            '&:focus': { borderColor: '#666666', backgroundColor: '#333333' },
                            '&.Mui-disabled': { borderColor: '#333333', color: '#666666' }
                          }}
                        >
                          Reset 2FA
                        </Button>
                      )}
                    </>
                  )}
                </DashboardCard>
              </motion.div>
            </AnimatePresence>

            {/* Navigation buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate(isVerified ? '/dashboard' : '/create-wallet')}
                disabled={isLoading}
                sx={{
                  borderRadius: '12px',
                  px: 3,
                  py: 1.5,
                  fontWeight: 500,
                  color: '#ffffff',
                  borderColor: '#333333',
                  '&:hover': { borderColor: '#666666', backgroundColor: '#333333' },
                  '&:focus': { borderColor: '#666666', backgroundColor: '#333333' },
                  '&.Mui-disabled': { borderColor: '#333333', color: '#666666' }
                }}
              >
                Back
              </Button>
            </Box>
          </motion.div>
        )}
      </Box>
    </Box>
  );
}

export default Setup2FA;